// Bunker Hill planning seed — Module 5 (word-doc §M5, worked example §5).
// Frozen at the same 08 Jun 2026 story moment as the rest of the seed;
// FD Rev 1 dates the plant to "Month 4, Year 1 — As-Commissioned – Ramp-Up",
// so Month 4 = June 2026 and Year 1 is back-anchored to March ([syn] dates).
//
// Provenance convention (seed-data rule R1): every literal cites the module
// doc section (A §M5 §n), the deck page (B: <page>), C3 (optiflow-ui.docx),
// or the corpus register (P<n>); anything with no source is [syn].
//
// The corpus P-register's three confidence levels are preserved, never
// flattened (corpus §3 note): Appendix B figures are documented fact; the
// Stretch rows and the 1,390 stpd forecast are Paul's own illustrations,
// flagged [illustrative]; the annual S&OP plan is honestly absent (OQ-P2).
//
// A6 Planning consumes PLANNING_MONTH_PROFILE, PLANNING_RECOVERY_SCENARIOS
// (Central and Conservative; D27 gives Optimistic the Capacity engine's
// recoveries), BUDGET_TARGETS and ANNUAL_PLAN_BUDGETS. M4_DAILY_ACTUALS is a
// probe baseline only (K16). Reports consumes FORECASTS. The corpus map is:
// docs/audits/2026-08-20-planning-corpus.md.

import type {
  BudgetTarget,
  Forecast,
  PlanningAnnualBudgetTargets,
  PlanningDailyActual,
  PlanningMonthProfile,
  PlanningPeriod,
  PlanningRecoveryScenario,
  StretchTarget,
} from '@/versions/v1/schema/planning';

export const DESIGN_THROUGHPUT_STPD = 1800;
export const DESIGN_OPERATING_DAYS = 350;
export const DESIGN_AVAILABILITY_PCT = 96.0;
export const ANNUAL_STRETCH_MULTIPLIER = 1.04;

/**
 * `[syn]` Year-1 monthly planning profile. The 1,440 stpd ramp target is held
 * flat for the year; planned shutdown days bring the operating calendar to
 * 350 days. Month 4 is the existing 1,440 × 30 = 43,200 st budget.
 */
export const PLANNING_MONTH_PROFILE: readonly PlanningMonthProfile[] = [
  { id: 'm1', label: 'M1 Mar 2026', month: 'Mar', year: 2026, calendarDays: 31, plannedShutdownDays: 3, budgetStpd: 1440, budgetZnRecoveryPct: 62.0, budgetPbRecoveryPct: 68.0, budgetAgRecoveryPct: 63.0, actualSt: 37000, actualZnRecoveryPct: 60.8, actualPbRecoveryPct: 65.0, actualAgRecoveryPct: 62.0, actualMillAvailabilityPct: 91.0, provenance: '[syn] Year-1 ramp profile; M1 actual below budget during commissioning ramp' },
  { id: 'm2', label: 'M2 Apr 2026', month: 'Apr', year: 2026, calendarDays: 30, plannedShutdownDays: 2, budgetStpd: 1440, budgetZnRecoveryPct: 65.0, budgetPbRecoveryPct: 70.0, budgetAgRecoveryPct: 66.0, actualSt: 38000, actualZnRecoveryPct: 63.0, actualPbRecoveryPct: 67.0, actualAgRecoveryPct: 65.0, actualMillAvailabilityPct: 92.4, provenance: '[syn] Year-1 ramp profile; M2 actual below budget during commissioning ramp' },
  { id: 'm3', label: 'M3 May 2026', month: 'May', year: 2026, calendarDays: 31, plannedShutdownDays: 2, budgetStpd: 1440, budgetZnRecoveryPct: 68.0, budgetPbRecoveryPct: 72.0, budgetAgRecoveryPct: 69.0, actualSt: 40000, actualZnRecoveryPct: 67.2, actualPbRecoveryPct: 70.8, actualAgRecoveryPct: 68.0, actualMillAvailabilityPct: 93.7, provenance: '[syn] Year-1 ramp profile; M3 actual below budget during commissioning ramp' },
  { id: 'm4', label: 'M4 Jun 2026', month: 'Jun', year: 2026, calendarDays: 30, plannedShutdownDays: 0, budgetStpd: 1440, budgetZnRecoveryPct: 69.8, budgetPbRecoveryPct: 75.0, budgetAgRecoveryPct: 71.6, actualMillAvailabilityPct: 95.2, provenance: '[syn] Month-4 budget profile; existing FD Month-4 budget targets; 1–7 Jun availability actual (complete production days, D25)' },
  { id: 'm5', label: 'M5 Jul 2026', month: 'Jul', year: 2026, calendarDays: 31, plannedShutdownDays: 1, budgetStpd: 1440, budgetZnRecoveryPct: 71.0, budgetPbRecoveryPct: 76.0, budgetAgRecoveryPct: 73.0, provenance: '[syn] Year-1 ramp profile; recovery budgets rise toward design' },
  { id: 'm6', label: 'M6 Aug 2026', month: 'Aug', year: 2026, calendarDays: 31, plannedShutdownDays: 1, budgetStpd: 1440, budgetZnRecoveryPct: 72.0, budgetPbRecoveryPct: 77.0, budgetAgRecoveryPct: 74.0, provenance: '[syn] Year-1 ramp profile; recovery budgets rise toward design' },
  { id: 'm7', label: 'M7 Sep 2026', month: 'Sep', year: 2026, calendarDays: 30, plannedShutdownDays: 1, budgetStpd: 1440, budgetZnRecoveryPct: 73.0, budgetPbRecoveryPct: 78.0, budgetAgRecoveryPct: 75.0, provenance: '[syn] Year-1 ramp profile; recovery budgets rise toward design' },
  { id: 'm8', label: 'M8 Oct 2026', month: 'Oct', year: 2026, calendarDays: 31, plannedShutdownDays: 1, budgetStpd: 1440, budgetZnRecoveryPct: 74.0, budgetPbRecoveryPct: 79.0, budgetAgRecoveryPct: 76.0, provenance: '[syn] Year-1 ramp profile; recovery budgets rise toward design' },
  { id: 'm9', label: 'M9 Nov 2026', month: 'Nov', year: 2026, calendarDays: 30, plannedShutdownDays: 1, budgetStpd: 1440, budgetZnRecoveryPct: 75.0, budgetPbRecoveryPct: 80.0, budgetAgRecoveryPct: 77.0, provenance: '[syn] Year-1 ramp profile; recovery budgets rise toward design' },
  { id: 'm10', label: 'M10 Dec 2026', month: 'Dec', year: 2026, calendarDays: 31, plannedShutdownDays: 1, budgetStpd: 1440, budgetZnRecoveryPct: 76.0, budgetPbRecoveryPct: 81.0, budgetAgRecoveryPct: 78.0, provenance: '[syn] Year-1 ramp profile; recovery budgets rise toward design' },
  { id: 'm11', label: 'M11 Jan 2027', month: 'Jan', year: 2027, calendarDays: 31, plannedShutdownDays: 1, budgetStpd: 1440, budgetZnRecoveryPct: 77.0, budgetPbRecoveryPct: 82.0, budgetAgRecoveryPct: 79.0, provenance: '[syn] Year-1 ramp profile; recovery budgets rise toward design' },
  { id: 'm12', label: 'M12 Feb 2027', month: 'Feb', year: 2027, calendarDays: 28, plannedShutdownDays: 1, budgetStpd: 1440, budgetZnRecoveryPct: 78.0, budgetPbRecoveryPct: 83.0, budgetAgRecoveryPct: 80.0, provenance: '[syn] Year-1 ramp profile; recovery budgets rise toward design' },
];

/** Scheduled mill hours across the synthetic Year 1 profile. */
export function yearOneScheduledHours(): number {
  return PLANNING_MONTH_PROFILE.reduce(
    (hours, month) => hours + (month.calendarDays - month.plannedShutdownDays) * 24,
    0,
  );
}

/** `[syn]` recovery assumptions for the remaining Year-1 forecast horizon.
 *  Planning reads the Central and Conservative rows; since D27 (K16) the
 *  Optimistic forecast carries the Capacity engine's Maximum achievable
 *  recoveries at its rate, so the Optimistic row here is not read by Planning. */
export const PLANNING_RECOVERY_SCENARIOS: readonly PlanningRecoveryScenario[] = [
  { name: 'Optimistic', recoveries: { pb: 88.2, zn: 85.1, ag: 84.2 }, provenance: '[syn] design recoveries carried through the remaining Year-1 forecast' },
  { name: 'Central', recoveries: { pb: 85.1, zn: 82.3, ag: 83.6 }, provenance: '[syn] 08 Jun recovery readings carried through the remaining Year-1 forecast' },
  { name: 'Conservative', recoveries: { pb: 83.0, zn: 78.0, ag: 80.0 }, provenance: '[syn] M12 Year-1 recovery budgets carried through the remaining forecast' },
];

/** `[syn]` typed Month-4 daily actual profile: 8 June days × 1,720 stpd = 13,760 st.
 *  D25 (K12c round 2): the *published* June MTD is no longer this typed sum — it
 *  is the shared `june-tonnage.derive.ts` read of the measured `rs-ore-td` series
 *  (complete production days 1–7 Jun, 12,401 dst). Since K16 the 7-day rate
 *  sample and the NSR leg read that measured series too, so no surface reads this
 *  profile: it is a probe baseline only (`Planning.probe.ts` logs it). */
export const M4_DAILY_ACTUALS: readonly PlanningDailyActual[] = [
  { date: '2026-06-01', shortTons: 1680, provenance: '[syn] Month-4 daily actual; ramp-up profile' },
  { date: '2026-06-02', shortTons: 1700, provenance: '[syn] Month-4 daily actual; ramp-up profile' },
  { date: '2026-06-03', shortTons: 1715, provenance: '[syn] Month-4 daily actual; ramp-up profile' },
  { date: '2026-06-04', shortTons: 1720, provenance: '[syn] Month-4 daily actual; ramp-up profile' },
  { date: '2026-06-05', shortTons: 1735, provenance: '[syn] Month-4 daily actual; ramp-up profile' },
  { date: '2026-06-06', shortTons: 1740, provenance: '[syn] Month-4 daily actual; ramp-up profile' },
  { date: '2026-06-07', shortTons: 1750, provenance: '[syn] Month-4 daily actual; ramp-up profile' },
  { date: '2026-06-08', shortTons: 1720, provenance: '[syn] Month-4 daily actual; matches rd-kpi-ore-td' },
];

/** `[syn]` annual operating budget; concentrate-grade targets reuse the GM table. */
export const ANNUAL_PLAN_BUDGETS: PlanningAnnualBudgetTargets = {
  millAvailabilityPct: 94.0,
  provenance: '[syn] Month-4 availability target carried across Year 1; stretch basis is +4%',
};
/** A §M5 §3: the cascade spine. One chain: Year 1 → Month 4 → the story week
 *  → the story day. All `manual` — no Pinnacle interface exists (A §M5 §8,
 *  OQ-P5). */
export const PLANNING_PERIODS: readonly PlanningPeriod[] = [
  {
    id: 'pp-y1',
    type: 'annual',
    label: 'Year 1 — As-Commissioned',
    startDate: '2026-03-01',
    endDate: '2027-02-28',
    inputForecastSource: 'manual',
    provenance:
      'FD Rev 1 ("Month 4, Year 1 — As-Commissioned – Ramp-Up") via A §M5 §5; dates [syn] back-anchored so Month 4 = Jun 2026 (B: title slide). No annual S&OP plan exists in the document set (A §M5 §7, OQ-P2); annual design capacity 657,000 st/yr assumes 100% utilisation — no availability factor specified anywhere (P4/OQ-P4)',
  },
  {
    id: 'pp-m4',
    type: 'monthly',
    label: 'Month 4 · Ramp-Up',
    startDate: '2026-06-01',
    endDate: '2026-06-30',
    parentPeriodId: 'pp-y1',
    inputForecastSource: 'manual',
    provenance:
      'FD App B table 94 — "Key Control Setpoints – Ramp-Up (Month 4, 80% of Design Rate)" via A §M5 §5',
  },
  {
    id: 'pp-w24',
    type: 'weekly',
    label: 'Week of 08 Jun 2026',
    startDate: '2026-06-08',
    endDate: '2026-06-14',
    parentPeriodId: 'pp-m4',
    inputForecastSource: 'manual',
    provenance: '[syn] the story-moment week (B: title slide, 08 Jun 2026)',
  },
  {
    id: 'pp-d0608',
    type: 'daily',
    label: '08 Jun 2026',
    startDate: '2026-06-08',
    endDate: '2026-06-08',
    parentPeriodId: 'pp-w24',
    inputForecastSource: 'manual',
    provenance: '[syn] the story moment (B: title slide)',
  },
];

const BUDGET_SETTER =
  'Management Support / GM (role per A §M5 §2 — no named owner in the document set, OQ-P2)';

/** A §M5 §5: Appendix B's Month-4 ramp-up figures landed as structured
 *  Budget data — "documented fact, not illustration". P1: the deck's Month-4
 *  ramp targets (~98% of design) disagree; the seed shows both, resolved on
 *  the client agenda, never locally. */
export const BUDGET_TARGETS: readonly BudgetTarget[] = [
  {
    id: 'bt-m4-throughput',
    periodId: 'pp-m4',
    parameter: 'Throughput',
    kpiId: 'kpi-ore-tpd',
    value: '1,440 stpd (60 stph) — 80% of design',
    setBy: BUDGET_SETTER,
    approvedBy: '',
    lockedAt:
      'FD Rev 1 — "Month 4, Year 1 — As-Commissioned – Ramp-Up" (issue date not in the corpus)',
    capacityCheck:
      '60 stph sits inside the 75 stph grinding design read — grinding is the plant bottleneck (M1 §8.3; u-grind)',
    provenance:
      'FD App B table 94 — WIC1201A operating point 60 stph vs 75 stph design (A §M5 §5) · P1: B ramp target is 1,800 t/d',
  },
  {
    id: 'bt-m4-pb-rec',
    periodId: 'pp-m4',
    parameter: 'Pb recovery',
    kpiId: 'kpi-pb-rec',
    value: '75.0% — 85% of design (design 88.2%, A PFS)',
    setBy: BUDGET_SETTER,
    approvedBy: '',
    lockedAt: 'FD Rev 1 (with the App B recovery note)',
    capacityCheck: '',
    provenance:
      'FD App B note — "Ramp-Up Target (Month 4) – 85% of design recovery" (A §M5 §5) · P1/P2: B ramp 85.0 on B design 87.0',
  },
  {
    id: 'bt-m4-zn-rec',
    periodId: 'pp-m4',
    parameter: 'Zn recovery',
    kpiId: 'kpi-zn-rec',
    value: '69.8% — 82% of design (design 85.1%, A PFS)',
    setBy: BUDGET_SETTER,
    approvedBy: '',
    lockedAt: 'FD Rev 1 (with the App B recovery note)',
    capacityCheck: '',
    provenance:
      'FD §8.1 Zn circuit — "82% of design recovery" (A §M5 §5) · P1/P2: B ramp 88.0 on B design 90.0',
  },
  {
    id: 'bt-m4-ag-rec',
    periodId: 'pp-m4',
    parameter: 'Ag recovery',
    kpiId: 'kpi-ag-rec',
    value: '71.6% — 85% of design (design 84.2%, A PFS)',
    setBy: BUDGET_SETTER,
    approvedBy: '',
    lockedAt: 'FD Rev 1 (with the App B recovery note)',
    capacityCheck: '',
    provenance:
      'FD App B note — "Ramp-Up Target (Month 4) – 85% of design recovery" (A §M5 §5) · P1/P2: B ramp 93.0 on B design 94.0',
  },
  {
    id: 'bt-w24-throughput',
    periodId: 'pp-w24',
    parameter: 'Throughput',
    kpiId: 'kpi-ore-tpd',
    value: '10,080 st for the week (1,440 stpd × 7 — straight pro-rata)',
    setBy: BUDGET_SETTER,
    approvedBy: '',
    lockedAt: '',
    capacityCheck: '',
    provenance:
      '[syn] pro-rata cascade per A §M5 §4 — no known-event calendar exists to adjust it (OQ-P6)',
  },
  {
    id: 'bt-d0608-throughput',
    periodId: 'pp-d0608',
    parameter: 'Throughput',
    kpiId: 'kpi-ore-tpd',
    value: '1,440 st for the day',
    setBy: BUDGET_SETTER,
    approvedBy: '',
    lockedAt: '',
    capacityCheck: '',
    provenance: '[syn] pro-rata cascade per A §M5 §4 (OQ-P6)',
  },
];

/** A §M5 §5: Stretch is a mechanism demonstration only — "no site policy
 *  defines what slightly above Budget means numerically at Bunker Hill"
 *  (P5/OQ-P3). Weekly/Daily horizons only (§4). */
export const STRETCH_TARGETS: readonly StretchTarget[] = [
  {
    id: 'st-w24-throughput',
    periodId: 'pp-w24',
    parameter: 'Throughput',
    kpiId: 'kpi-ore-tpd',
    value: '≈1,500 stpd for the week',
    basis: '≈ +4% over the Month-4 Budget — illustrative',
    setBy: 'Metallurgy/Operations teams (A §M5 §2) — the actual figure is their policy decision',
    capacityCheck:
      '≈62.5 stph — still inside the 75 stph grinding design read (M1 §8.3; u-grind)',
    provenance:
      'A §M5 §5 — "shown illustratively only… a mechanism demonstration, not a real number" · [illustrative — no site policy] (P5/OQ-P3)',
  },
  {
    id: 'st-d0608-throughput',
    periodId: 'pp-d0608',
    parameter: 'Throughput',
    kpiId: 'kpi-ore-tpd',
    value: '≈1,500 st for the day',
    basis: '≈ +4% over the Month-4 Budget — illustrative',
    setBy: 'Metallurgy/Operations teams (A §M5 §2) — the actual figure is their policy decision',
    capacityCheck: '',
    provenance:
      'A §M5 §5 · [illustrative — no site policy] (P5/OQ-P3)',
  },
];

/** A §M5 §2/§6 + C3 §7: the Latest Estimate rows, each with a legible driver
 *  chain (corpus §2.5) — reconciled actuals + plan remainder ± open cases.
 *  Narrative, never computed. */
export const FORECASTS: readonly Forecast[] = [
  {
    id: 'fc-m4-throughput',
    periodId: 'pp-m4',
    parameter: 'Throughput',
    kpiId: 'kpi-ore-tpd',
    value: '1,390 stpd',
    forecastDate: '08 Jun 2026',
    varianceToBudget: '−3.5% vs Budget 1,440 stpd',
    drivers: [
      {
        kind: 'reconciled-actuals',
        ref: '',
        note: 'Actuals-to-date roll in from Module 2 reconciled figures as they land (A §M5 §6)',
      },
      {
        kind: 'plan-remainder',
        ref: '',
        note: 'Remainder of Month 4 at the ramp-up plan rate (A §M5 §4)',
      },
    ],
    provenance:
      'A §M5 §2 — "Budget 1,440 stpd, currently forecasting 1,390 stpd" · [illustrative]',
  },
  {
    id: 'fc-w24-zn-rec',
    periodId: 'pp-w24',
    parameter: 'Zn recovery',
    kpiId: 'kpi-zn-rec',
    value: '89–91%',
    forecastDate: '08 Jun 2026',
    varianceToBudget: '',
    drivers: [
      {
        kind: 'open-case',
        ref: 'case-p80',
        note: 'Classification stability — the open cyclone O/F P80 case (C3 §7: "prioritising classification stability")',
      },
    ],
    provenance:
      'C3 §7 Forecasting Snapshot — 7-day forecast · P1: band sits on the C3/deck basis (target Zn 90%), not the App-B Budget',
  },
  {
    id: 'fc-w24-pb-rec',
    periodId: 'pp-w24',
    parameter: 'Pb recovery',
    kpiId: 'kpi-pb-rec',
    value: '83–85%',
    forecastDate: '08 Jun 2026',
    varianceToBudget: '',
    drivers: [
      {
        kind: 'open-case',
        ref: 'case-p80',
        note: 'Classification stability — the open cyclone O/F P80 case (C3 §7)',
      },
    ],
    provenance:
      'C3 §7 Forecasting Snapshot — 7-day forecast · P1: band sits on the C3/deck basis (target Pb 84%), not the App-B Budget',
  },
  {
    id: 'fc-m4-metal',
    periodId: 'pp-m4',
    parameter: 'Metal production',
    kpiId: '',
    value: '1–3% below plan unless cyclone efficiency improves',
    forecastDate: '08 Jun 2026',
    varianceToBudget: '',
    drivers: [
      {
        kind: 'reconciled-actuals',
        ref: 'rv-zn-metal',
        note: 'Month-to-date contained metal from Module 2 reconciled figures (A §M5 §6)',
      },
      {
        kind: 'open-case',
        ref: 'case-p80',
        note: 'Open Cases affecting production adjust the current Forecast (A §M5 §6)',
      },
    ],
    provenance: 'C3 §7 Forecasting Snapshot — 30-day forecast',
  },
];

// PLANNING_TARGETS (the KPI-side read-model projection of BUDGET_TARGETS /
// FORECASTS above) was deleted in pl.D1 round 3: it never merged into
// TARGETS (bunker-hill-advisory-seed.ts) — no such merge exists anywhere in
// src/ — and after round 2 relabelled TARGETS's own rows to 'budget', it had
// zero consumers left while holding a second copy of the same four Month-4
// figures BUDGET_TARGETS already carries. See docs/logic/planning/overview.md.
